// src/services/poc/cidDownloadLogger.service.impl.ts

import { CidDownloadLogger, DOWNLOAD_STEP_LABELS, type DownloadStep } from '@/services/abstract/poc/cidDownloadLogger.interface';
import { createLogger, type Logger } from '@/utils/logger';
import { BYTES_PER_KB, MS_PER_SEC } from '@/utils/downloadProgress';

const CID_PREVIEW_LENGTH = 20;
const STEP_PAD_LENGTH = 22;
const DECIMAL_PLACES = 2;
const BYTES_PER_MB = BYTES_PER_KB * BYTES_PER_KB;

const BYTE_KEYS = new Set(['totalBytes', 'expectedSize', 'bytesLoaded', 'originalSize']);
const MS_KEYS = new Set(['durationMs', 'waitTimeMs']);

/**
 * Console-based CID Download Logger Implementation
 *
 * Logs download progress to the browser console using the existing
 * logger infrastructure. Follows the same pattern as HAP's CidUploadLogger.
 */
export class CidDownloadLoggerImpl extends CidDownloadLogger {
  private readonly logger: Logger;

  constructor() {
    super();
    this.logger = createLogger({ namespace: 'six3ro:p2p:files:download-progress' });
  }

  log(cid: string, step: DownloadStep, details?: Record<string, unknown>): void {
    const timestamp = new Date().toISOString();
    const stepLabel = DOWNLOAD_STEP_LABELS[step].padEnd(STEP_PAD_LENGTH);
    const cidPreview = cid.length > CID_PREVIEW_LENGTH ? `${cid.substring(0, CID_PREVIEW_LENGTH)}...` : cid;

    const formattedDetails = details !== undefined && Object.keys(details).length > 0
      ? CidDownloadLoggerImpl.formatDetails(details)
      : '';

    // Format matches HAP's CidUploadLogger: [timestamp] stepLabel CID: preview details
    const message = `[${timestamp}] ${stepLabel} CID: ${cidPreview}${formattedDetails}`;

    if (step === 'error') {
      this.logger.error(message);
    } else
      this.logger.info(message);
  }

  private static formatDetails(details: Record<string, unknown>): string {
    const parts = Object.entries(details)
      .map(([key, value]) => {
        if (BYTE_KEYS.has(key) && typeof value === 'number') {
          const mb = value / BYTES_PER_MB;
          return `${key}=${mb.toFixed(DECIMAL_PLACES)}MB`;
        }
        if (MS_KEYS.has(key) && typeof value === 'number') {
          const sec = value / MS_PER_SEC;
          return `${key}=${sec.toFixed(DECIMAL_PLACES)}s`;
        }
        return `${key}=${CidDownloadLoggerImpl.formatValue(value)}`;
      })
      .join(' | ');
    return ` ${parts}`;
  }

  private static formatValue(value: unknown): string {
    if (value === null || value === undefined) {
      return 'null';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'number') {
      if (Number.isInteger(value)) {
        return value.toString();
      }
      return value.toFixed(DECIMAL_PLACES);
    }
    if (typeof value === 'boolean') {
      return value ? 'true' : 'false';
    }
    if (value instanceof Error) {
      return value.message;
    }
    return JSON.stringify(value);
  }
}
